import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Dayjs } from 'dayjs';
import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import { BookingModeDto, DictionaryTypeDto } from '@/api/generated';
import { DateFormField } from '@/components/form/DateFormField.comp.tsx';
import { DateTimeFormField } from '@/components/form/DateTimeFormField.comp.tsx';
import { SelectSearchOptionModel } from '@/components/form/model/SelectSearchOption.model.ts';
import { SelectSearchFormField } from '@/components/form/SelectSearchFormField.comp.tsx';
import { BUSINESS_TIME_ZONE } from '@/utils/reservationRange.util.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { ProcessCalendarEntryQueryFormModel } from '@/views/processes/calendarEntryQuery/model/ProcessCalendarEntryQueryForm.model.ts';
import { processCalendarEntryQueryUtil } from '@/views/processes/calendarEntryQuery/utils/processCalendarEntryQuery.util.ts';

import { processCalendarEntryQueryFormStyles as styles } from './ProcessCalendarEntryQueryForm.styles.ts';

type Props = {
  bookingMode: BookingModeDto;
  disabled: boolean;
  entryOptions: SelectSearchOptionModel<number>[];
  /** The booking mode of the selected type failed to load, so the form cannot be saved. */
  isBookingModeError?: boolean;
  typeOptions: SelectSearchOptionModel<DictionaryTypeDto>[];
};

/**
 * Pola zapytania o wpis kalendarzowy, współdzielone przez dialog tworzenia procesu
 * i edycję w nagłówku szczegółów procesu. Wszystkie pola są opcjonalne.
 */
export const ProcessCalendarEntryQueryForm: React.FC<Props> = ({
  bookingMode,
  disabled,
  entryOptions,
  isBookingModeError,
  typeOptions,
}) => {
  const { t } = useTranslationWithPrefix('views.processes.calendarEntryQuery');
  const { getValues, setValue, watch } = useFormContext<ProcessCalendarEntryQueryFormModel>();
  const selectedType = watch('requestedDictionaryType');
  const selectedEntry = watch('requestedDictionaryId');
  const loadedEntry = entryOptions.find(option => option.value === selectedEntry?.value);

  // prefilled entries may come without a label; take it from the loaded options
  useEffect(() => {
    if (loadedEntry && loadedEntry.label !== selectedEntry?.label) {
      setValue('requestedDictionaryId', loadedEntry);
    }
  }, [loadedEntry, selectedEntry?.label, setValue]);

  const isExactTime = bookingMode === BookingModeDto.ExactTime;
  const handleFromChange = (value: Dayjs | null) => {
    if (value && !getValues('requestedTo')) {
      setValue('requestedTo', isExactTime ? value.add(1, 'hour') : value, { shouldDirty: true });
    }
  };
  const DateField = isExactTime ? DateTimeFormField : DateFormField;

  return (
    <Stack component="fieldset" spacing={0.5} sx={styles.fieldset}>
      <Typography component="legend" variant="f14Medium">
        {t('sectionTitle')}
      </Typography>
      <Typography color="textSecondary" variant="f12Regular">
        {t('sectionHint')}
      </Typography>
      <Grid container spacing={2} sx={{ pt: 1 }}>
        <Grid size={{ md: 6, xs: 12 }}>
          <SelectSearchFormField<
            ProcessCalendarEntryQueryFormModel,
            'requestedDictionaryType',
            DictionaryTypeDto
          >
            disabled={disabled}
            label={t('dictionaryType')}
            name="requestedDictionaryType"
            onChange={() => setValue('requestedDictionaryId', null, { shouldDirty: true })}
            options={typeOptions}
            placeholder={t('dictionaryTypePlaceholder')}
          />
        </Grid>
        <Grid size={{ md: 6, xs: 12 }}>
          <SelectSearchFormField<
            ProcessCalendarEntryQueryFormModel,
            'requestedDictionaryId',
            number
          >
            disabled={disabled || !selectedType}
            label={t('dictionaryEntry')}
            name="requestedDictionaryId"
            options={processCalendarEntryQueryUtil.withCurrentOption(entryOptions, selectedEntry)}
            placeholder={selectedType ? t('dictionaryEntryPlaceholder') : t('dictionaryTypeFirst')}
          />
        </Grid>
        <Grid size={{ md: 6, xs: 12 }}>
          <DateField<ProcessCalendarEntryQueryFormModel, 'requestedFrom'>
            disabled={disabled}
            label={t('from')}
            name="requestedFrom"
            onChangeSideEffect={handleFromChange}
            placeholder={t('datePlaceholder')}
            timezone={BUSINESS_TIME_ZONE}
          />
        </Grid>
        <Grid size={{ md: 6, xs: 12 }}>
          <DateField<ProcessCalendarEntryQueryFormModel, 'requestedTo'>
            disabled={disabled}
            label={t('to')}
            name="requestedTo"
            placeholder={t('datePlaceholder')}
            timezone={BUSINESS_TIME_ZONE}
          />
        </Grid>
      </Grid>
      {isBookingModeError && (
        <Typography color="error" role="alert" variant="f12Regular">
          {t('bookingModeError')}
        </Typography>
      )}
    </Stack>
  );
};
